<template>
  <list-layout :tableTools="toggleTableTools">
    <template slot="header">
      <BaseHeader :content="header" />
    </template>
    <template slot="table-core">
      <base-table-pagination :total="pagination.total" :currentPage="pagination.currentPage"
        :perPage="pagination.pageSize" @changeCurrentPage="changeCurrentPage"
        @changePerPage="changePerPage"></base-table-pagination>
      <div class="flex flex-row w-100">
        <base-table-searches @toggleSearchesDialog="toggleSearchesDialog" />
        <base-table-search @toggleFilters="toggleFilters" />
      </div>
    </template>
    <template slot="table-tags">
      <base-table-filter-tags :tags="tags" @removeTag="removeFilterTagHanlder" @removeAllTags="removeAllTagsHandler"
        @saveTags="saveTagsHandler" />
    </template>
    <template slot="table-component">
      <kendo-grid-component
        ref="grid"
        v-model="selected"
        value-key="IdSociedad"
        :id-vista="idVista"
        :spanish="spanish"
        :data='data'
        :kendo="kendo"
        :loading='isGeneralLoading'
        :selectedField="selectedField"
        @actionclick='openPath'
        @datastatechange='dataStateChange'>
      </kendo-grid-component>
    </template>
    <template slot="layout-extend">
      <base-footer>
        <template slot="actions">
          <base-button-responsive mode='primary' :text="labelBtnExcel" icon='excelxls' @click='exportExcel'
            :loading='isGeneralLoading || loadingExcel' :disabled="loadingExcel" />
          <base-button-responsive v-if="hasModuleImportaciones && !isRoleUserOrConsulta" mode='primary' 
            :text="$t('app.import')" icon='import' @click='toggleImportSociedades' 
            :loading='isGeneralLoading || loadingExcel' :disabled="loadingExcel"/>
          <base-button-responsive v-if="!isRoleUserConsultaProvider()" mode="primary" :text="$t('agenda.new_sociedad')" icon="plus" 
            @click="goToNewContact" :loading="isGeneralLoading" />
        </template>
      </base-footer>
      <base-table-select-actions-expand :visibility="hasSelected" :selected="rowsSelected" :total="rowsTotal" :baseDropDownVisible="getAccionesPosibles.length > 0">
        <template slot="commons">
          <base-button size="xs" mode="tools" @click="unselect">
            <base-icon name="vall" size="sm" class="mr-1" />
            {{$t('app.unselect')}}
          </base-button>
        </template>
        <template slot="more">
          <span v-for="action in getAccionesPosibles" :key="action.IdAccion">
            <base-button-item-list theme="dark" @click="executeAction(action)">
              <base-icon :name="selectIcon(action.IdAccion)" class="mr-2" />
              {{ action.Descripcion }}
            </base-button-item-list>
          </span>
        </template>
      </base-table-select-actions-expand>
      <contacto-grouping-dialog 
        v-if="!isRoleUserConsultaProvider()"
        :dialogOpen="showModalGroupingSeller" 
        @toggleDialog="toggleModalGroupingSeller" 
        @applyDialog="applyModalGroupingSeller"
        :header="$t('grouping.link_to_group')"
        :headerValue="$t('grouping.group_name')"
        :idSociedad="selectedIdSociedad"
        :idRolContact=1
        />
      <contacto-grouping-dialog 
        v-if="!isRoleUserConsultaProvider()"
        :dialogOpen="showModalGroupingBuyer"
        @toggleDialog="toggleModalGroupingBuyer"
        @applyDialog="applyModalGroupingBuyer"
        :header="$t('grouping.link_to_group')"
        :headerValue="$t('grouping.group_name')" 
        :idSociedad="selectedIdSociedad"
        :idRolContact=2
        />
      <Dialog :title="$t('agenda.delete_sociedades')" :visible.sync="showModal" width="40%" custom-class="min-width-dialog" center>
        <span class="text-center w-full inline-block dark:text-darkblue-200 word-break-n">{{ processMessagedeleteContacto
        }}</span>
        <template slot="footer">
          <div class="flex flex-col lg:flex-row w-full lg:w-auto items-center space-y-2 lg:space-y-0 lg:space-x-2">
            <base-button class="w-full" @click="showModal = false" mode="secondary-phantom-blue"
              :text="$t('app.cancel')" />
            <base-button class="w-full" mode="secondary-phantom-blue" :text="$t('app.apply')"
              @click="() => { deleteHandler(); showModal = false }" />
          </div>
        </template>
      </Dialog>
      <import-generic-dialog
        :dialogOpen="showModalImportSociedades"
        @toggleDialog="toggleImportSociedades"
        @applyDialog="applyImportSociedades"
        @downloadTemplate="downloadTemplateSociedades"
        :header="$t('imports.import_sociedades_header')" 
        />
      <filter-panel 
        :panelOpen="filterPanel" 
        @togglePanel="toggleFilters" 
        @applyFilters="applyFiltersHandler" 
        :vuexnamespace="'sociedades'"
        />
      <create-search-dialog :numberSearches="numberSearches" :idVista="idVista" :dialogOpen="saveSearchDialog"
        :tags="tags" @toggleDialog="toggleDialogSearches" @applyDialog="applySearchHandler" />
      <filter-panel-searches ref="filterPanelSearches" :idVista="idVista" :vuexnamespace="vuexnamespace"
        :panelOpen="searchesPanel" @togglePanel="toggleSearchesDialog" @applyFilters="applyFiltersHandler"
        @updateNumberSearches="updateNumberSearches" />
    </template>
  </list-layout>
</template>

<script>
import { bus } from '@/main'
import { i18n } from '@/i18n'
import { Dialog } from 'element-ui'

import KendoGridComponent from '@/components/KendoGridComponentV2.vue'
import ListLayout from '@/components/layout/ListLayout.vue'
import FilterPanel from '@/components/filters/SociedadesFilter.vue'
import FilterPanelSearches from '@/components/searches/SearchesPanel.vue'
import ContactoGroupingDialog from '@/components/ContactoGroupingDialog.vue'
import CreateSearchDialog from '@/components/searches/CreateSearchDialog.vue'

import { me } from '@/services/auth/me'
import { getSociedadesPag } from '@/services/configurations/getSociedadesPag'
import { getSociedadesExcel } from '@/services/configurations/getSociedadesExcel'
import { deleteSociedad } from '@/services/configurations/deleteSociedad'
import { addAgrupacionCliente } from '@/services/configurations/grouping/addAgrupacionCliente'
import { addAgrupacionClienteNombre } from '@/services/configurations/grouping/addAgrupacionClienteNombre'
import { addAgrupacionProveedor } from '@/services/configurations/grouping/addAgrupacionProveedor'
import { addAgrupacionProveedorNombre } from '@/services/configurations/grouping/addAgrupacionProveedorNombre'

// Imports
import ImportGenericDialog from '@/components/ImportGenericDialog.vue'
import { importSociedades } from '@/services/configurations/imports/importSociedades'
import { downloadTemplateSociedades } from '@/services/configurations/imports/downloadTemplateSociedades'

export default {
  name: 'vista-sociedades',
  components: {
    ListLayout,
    FilterPanel,
    FilterPanelSearches,
    KendoGridComponent,
    CreateSearchDialog,
    ContactoGroupingDialog,
    Dialog,
    ImportGenericDialog,
  },
  data: function () {
    return {
      heightSelect: 0,
      heightInput: 35,
      idVista: 57,
      // Searches
      numberSearches: 0,
      saveSearchDialog: false,
      searchesPanel: false,
      // Import
      showModalImportSociedades: false,
      // All
      showModalGroupingSeller: false,
      showModalGroupingBuyer: false,
      loadingExcel: false,
      spanish: true,
      showModal: false,
      header: {},
      vuexnamespace: 'sociedades',
      hasClientFilterData: false,
      selectedField: 'selected',
      filterPanel: false,
      showAllRows: true,
      loadingDataTable: false,
      data: null,
      selected: [],
      listaAcciones: [
        { IdAccion: 0, Descripcion: i18n.t('app.remove') },
        { IdAccion: 12, Descripcion: i18n.t('grouping.add_grouping_sellers') },
        { IdAccion: 13, Descripcion: i18n.t('grouping.add_grouping_buyers') },
      ],
      processMessagedeleteContacto: i18n.t('app.delete_selected_confirmation_f', { entity: i18n.t('agenda.sociedad_plural').toLowerCase() }),
    }
  },
  computed: {
    isRoleUserOrConsulta: function () {
      return this.role === 1 || this.role === 4 || this.role === 9
    },
    hasModuleImportaciones () {
      return this.$store.getters['auth/hasImportacionesModule']
    },
    hasAnyImportaciones () {
      return this.$store.getters['auth/hasAnyImportaciones']
    },
    isClientFilterActive () {
      return this.$store.getters[this.vuexnamespace + '/isClientFilterActive']
    },
    actualFilterNav () {
      return this.$store.getters['app/idClienteFilterNav']
    },
    labelBtnExcel: function () {
      if (this.hasSelected) return `${i18n.t('app.export_excel')} (${i18n.t('app.selected')})`
      return i18n.t('app.export_excel')
    },
    kendo: function () {
      return this.$store.getters[this.vuexnamespace + '/getKendo']
    },
    isLoadingSelect: function () {
      return this.loadingActionUnSelect || this.loadingActionSelect
    },
    isHeadCoreReady: function () {
      return this.$store.getters.isHeadCoreReady
    },
    toggleTableTools: function () {
      return this.selected.length > 0
    },
    rowsSelected: function () {
      return this.selected.length
    },
    rowsTotal: function () {
      return this.selected.length
    },
    masterFilters: function () {
      return this.$store.getters[this.vuexnamespace + '/getMasterFilters']
    },
    isGeneralLoading: function () {
      return this.$store.getters.isLoading
    },
    headerCoreFilters: function () {
      return this.$store.getters['auth/queryHeaders']
    },
    tags: function () {
      return this.$store.getters[this.vuexnamespace + '/getTags']
    },
    pagination: function () {
      return this.$store.getters[this.vuexnamespace + '/getPaginationInfo']
    },
    areAllSelected () {
      return this.data.length === this.selected.length
    },
    hasSelected () {
      return this.selected.length > 0
    },
    selectIcon: function (action) {
      return (action) =>
        action === 0 ? 'trash'
          : action === 12 ? 'groupingadd'
            : action === 13 ? 'groupingadd'
              : 'unlink'
    },
    selectedIdSociedad () {
      return this.hasSelected ? this.selected[0] : null
    },
    hasModuleAgrupaciones () {
      return this.$store.getters['auth/hasAgrupacionesModule']
    },
    role: function () {
      return this.$store.getters['auth/role']
    },
    getAccionesPosibles () {
      let accionesFinal = []
      if (!this.isRoleUserConsultaProvider()) { 
        accionesFinal = this.listaAcciones 
        if (this.hasSelected) {
          // const idSociedad = this.selectedIdSociedad
          // || this.selected.filter(x => x.IdSociedad === idSociedad).length !== this.selected.length) 
          if (this.role === 1 || this.role === 9) {
            accionesFinal = accionesFinal.filter(x => x.IdAccion !== 12 && x.IdAccion !== 13 && x.IdAccion !== 0)
          } else {
            if (!this.hasModuleAgrupaciones) accionesFinal = accionesFinal.filter(x => x.IdAccion !== 12 && x.IdAccion !== 13)
            // else {
            // if (this.selected.filter(x => x.idRol.some(rol => rol === 1)).length !== this.selected.length) {
            //   accionesFinal = accionesFinal.filter(x => x.IdAccion !== 12)
            // }
            // if (this.selected.filter(x => x.idRol.some(rol => rol === 2)).length !== this.selected.length) {
            //   accionesFinal = accionesFinal.filter(x => x.IdAccion !== 13)
            // }
            // }
          }
        }
      }
      return accionesFinal
    },
  },
  watch: {
    $route (to, from) {
      this.updateOnRouteChange(to, from)
      // this.routerChangeHandler()
    },
    headerCoreFilters: {
      // immediate: true,
      deep: true,
      handler: 'headerCoreFiltersHandler'
    },
  },
  created: async function () {
    if (this.isRoleUserConsultaProvider()) {
      this.header = {
        title: i18n.t('companies.header'),
        icon: 'companies',
        breadcrumb: null,
        url: null
      }
    } else {
      this.header = {
        title: i18n.t('agenda.sociedad_plural'),
        icon: 'sociedades',
        breadcrumb: null,
        url: null
      }
    }

    if (this.$route.params.data) {
      this.filterByParamsData()
    }
  },
  mounted: async function () {
    this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
    // this.$store.commit(this.vuexnamespace + '/resetMasterFilters')
    this.$store.commit(this.vuexnamespace + '/setPageSize', this.kendo.take)
    if (this.actualFilterNav && this.isClientFilterActive && !this.hasClientFilterData) {
      const filter = this.masterFilters
      filter.Cliente = { label: i18n.t('clients.entity_name'), value: this.actualFilterNav }
      this.$store.commit(this.vuexnamespace + '/setMasterFilters', filter)
    } else if (this.actualFilterNav === null) {
      await this.removeAllTagsHandler()
    }
    this.data = await this.applyFilters()
  },
  methods: {
    toggleImportSociedades () {
      this.showModalImportSociedades = !this.showModalImportSociedades
    },
    applyImportSociedades: async function (file, form) {
      this.$store.commit('setIsLoading', true)
      try {
        const response = await importSociedades(file, { idCliente: form.idCliente })
        if (response.blnError) {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
        } else {
          this.toggleImportSociedades() 
          this.doFeedback(i18n.t('imports.processing'), 'info')
          if (!this.hasAnyImportaciones) {
            bus.$emit('resetAndUpdateNav', 1000)
          }
          this.$router.push({ name: 'configurations/imports' })
        } 
      } catch (error) {
        console.error(error)
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    downloadTemplateSociedades: async function () {
      this.$store.commit('setIsLoading', true)
      try {
        const response = await downloadTemplateSociedades()
        if (response.blnError) {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
        } else {
          this.downloadBlob(response, 'plantilla-importacion-sociedades.xlsx')
        }
      } catch (error) {
        console.error(error)
        if (error.response?.data) {
          const errorText = await error.response.data.text()
          this.doFeedback(errorText)
        } else {
          this.doFeedback()
        }
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    getClienteId: async function () {
      const data = await me()
      return parseInt(data.respuesta.idCliente)
    },
    // isRoleProvider: function () {
    //   return this.role === 9
    // },
    isRoleUserConsultaProvider: function () {
      return this.role === 1 || this.role === 4 || this.role === 9
    },
    toggleModalGroupingSeller () {
      this.showModalGroupingSeller = !this.showModalGroupingSeller
    },
    toggleModalGroupingBuyer () {
      this.showModalGroupingBuyer = !this.showModalGroupingBuyer
    },

    // PROVEEDOR
    applyModalGroupingSeller: async function (nombre, newGroup = false) {
      try {
        this.$store.commit('setIsLoading', true)
        const clientId = await this.getClienteId()
        const payload = { Nombre: nombre, idsSociedad: this.selected || [], idCliente: clientId }
        
        if (newGroup) {
          const response = await addAgrupacionProveedor(payload)
          if (response.blnError === true) {
            this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
          } else {
            this.doFeedback(i18n.t('feedback.success.action_tramited_msg'), 'success')
            this.toggleModalGroupingSeller()
            await this.applyFilters()
          }
        } else {
          const response = await addAgrupacionProveedorNombre(payload)
          if (response.blnError === true) {
            this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
          } else {
            this.doFeedback(i18n.t('feedback.success.action_tramited_msg'), 'success')
            this.toggleModalGroupingSeller()
            await this.applyFilters()
          }
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },

    // CLIENTE
    applyModalGroupingBuyer: async function (nombre, newGroup = false) {
      try {
        this.$store.commit('setIsLoading', true)
        const clientId = await this.getClienteId()
        const payload = { Nombre: nombre, idsSociedad: [], idCliente: clientId }
        this.selected.forEach((s) => {
          payload.idsSociedad.push(s.IdSociedad)
        })
        if (newGroup) {
          const response = await addAgrupacionCliente(payload)
          if (response.blnError === true) {
            this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
          } else {
            this.doFeedback(i18n.t('feedback.success.action_tramited_msg'), 'success')
            this.toggleModalGroupingBuyer()
            await this.applyFilters()
          }
        } else {
          const response = await addAgrupacionClienteNombre(payload)
          if (response.blnError === true) {
            this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
          } else {
            this.doFeedback(i18n.t('feedback.success.action_tramited_msg'), 'success')
            this.toggleModalGroupingBuyer()
            await this.applyFilters()
          }
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    filterByParamsData: function () {
      const data = this.$route.params.data
      const filters = {}
      if (data) {
        data.filters.forEach(filter => {
          if (filter.type === 'cliente') {
            this.hasClientFilterData = true
            const cliente = { label: i18n.t('clients.entity_name'), value: { Nombre: filter.data.label, idCliente: filter.data.id } }
            filters.empresa = cliente
          } else {
            filters[filter.type] = filter.data
          }
        })
        this.$store.commit(this.vuexnamespace + '/setMasterFilters', filters)
      }
    },
    openPath: function (event) {
      this.$store.commit('app/setheaderBreadCrumb', event.dataItem.IdSociedad)
      this.$router.push({ name: 'masters/society/details', params: { id: event.dataItem.IdSociedad, selfInvoice: event.dataItem.IsSeflInvoiceSociety } })
    },
    toggleFilters: function () {
      this.filterPanel = !this.filterPanel
    },
    toggleSearchesDialog: function () {
      this.searchesPanel = !this.searchesPanel
    },
    removeFilterTag: function (tag) {
      this.tags.splice(this.tags.indexOf(tag), 1)
    },
    dataStateChange: async function (event) {
      this.data = await this.applyFilters()
    },
    exportExcel: async function () {
      this.$store.commit('app/setLoading', true)
      this.loadingExcel = true
      const data = this.$store.getters[this.vuexnamespace + '/getAppliedFilters']
      const params = {
        idCliente: data.Cliente ? data.Cliente.value.idCliente : null,
        NameCorporateName: data.NameCorporateName ? data.NameCorporateName.value : null,
        TIN: data.TIN ? data.TIN.value : null,
        FirstSurname: data.FirstSurname ? data.FirstSurname.value : null,
        SecondSurname: data.SecondSurname ? data.SecondSurname.value : null,
        ResidenceType: data.ResidenceType ? data.ResidenceType.value : null,
        PersonType: data.PersonType ? data.PersonType.value : null,
        Locations: data.Locations ? data.Locations.value : null,
        LinkedCompanies: data.LinkedCompanies ? data.LinkedCompanies.value : null,
        Book: data.Book ? data.Book.value : null,
        RegisterOfCompaniesLocation: data.RegisterOfCompaniesLocation ? data.RegisterOfCompaniesLocation.value : null,
        Sheet: data.Sheet ? data.Sheet.value : null,
        Folio: data.Folio ? data.Folio.value : null,
        Section: data.Section ? data.Section.value : null,
        Volume: data.Volume ? data.Volume.value : null,
        AdditionalData: data.AdditionalData ? data.AdditionalData.value : null,
      }
      const objOrder = this.getObjOrderMixin(this.kendo.group, this.kendo.sort)
      try {
        const objColumnas = this.getColumnsGroupExcelMixin(this.kendo.columns, this.kendo.group)
        const payload = { order: objOrder.order, grupos: objColumnas }
        if (this.hasSelected) {
          payload.listaIDs = this.selected
        }
        const response = await getSociedadesExcel(payload, params)
        const blob = new Blob([response.data], { type: response.data.type, encoding: 'UTF-8' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        let filename = `${i18n.t('agenda.sociedad_plural').toLowerCase()}.xlsx`
        filename = decodeURI(filename)
        link.download = filename
        link.click()
      } catch (error) {
        console.error(error)
        if (error.response?.data) {
          const errorText = await error.response.data.text()
          this.doFeedback(errorText)
        } else {
          this.doFeedback()
        }
      } finally {
        this.$store.commit('app/setLoading', false)
        this.loadingExcel = false
      }
    },
    updateOnRouteChange: async function (to, from) {
      await this.applyFilters()
    },
    applyFilters: async function (eventData) {
      this.loadingDataTable = true
      const data = this.$store.getters[this.vuexnamespace + '/getAppliedFilters']
      this.$store.commit(this.vuexnamespace + '/setClientFilterActive', !!((data.Cliente && data.Cliente.value.idCliente === this.actualFilterNav?.idCliente) || (this.hasClientFilterData)))
      const params = {
        idCliente: data.Cliente ? data.Cliente.value?.idCliente : null,
        NameCorporateName: data.NameCorporateName ? data.NameCorporateName.value : null,
        TIN: data.TIN ? data.TIN.value : null,
        FirstSurname: data.FirstSurname ? data.FirstSurname.value : null,
        SecondSurname: data.SecondSurname ? data.SecondSurname.value : null,
        ResidenceType: data.ResidenceType ? data.ResidenceType.value : null,
        PersonType: data.PersonType ? data.PersonType.value : null,
        Locations: data.Locations ? data.Locations.value : null,
        LinkedCompanies: data.LinkedCompanies ? data.LinkedCompanies.value : null,
        Book: data.Book ? data.Book.value : null,
        RegisterOfCompaniesLocation: data.RegisterOfCompaniesLocation ? data.RegisterOfCompaniesLocation.value : null,
        Sheet: data.Sheet ? data.Sheet.value : null,
        Folio: data.Folio ? data.Folio.value : null,
        Section: data.Section ? data.Section.value : null,
        Volume: data.Volume ? data.Volume.value : null,
        AdditionalData: data.AdditionalData ? data.AdditionalData.value : null,
      }
      const objOrder = this.getObjOrderMixin(this.kendo.group, this.kendo.sort)
      try {
        const data = await this.getItemsData(objOrder, params)
        return data
      } catch (error) {
        this.$store.commit('setIsLoading', false)
        this.doFeedback(i18n.t('feedback.error.general'))
      }
      return this.data
    },
    getItemsData: async function (objOrder, filters) {
      this.$store.commit('setAjaxRequestPending', true)
      this.$store.commit('setIsLoading', true)
      const params = {
        paginate: true,
        results: this.pagination.pageSize,
        page: this.pagination.currentPage,
        ...filters
      }
      const res = await this.getMainData(objOrder, params)
      this.data = res.respuesta?.map((item) => {
        item.FirstSurname = item.FirstSurname || ''
        item.SecondSurname = item.SecondSurname || ''
        item.RegistrationData = item.RegistrationData || {}
        item.RegistrationData.Book = item.RegistrationData.Book || ''
        item.RegistrationData.RegisterOfCompaniesLocation = item.RegistrationData.RegisterOfCompaniesLocation || ''
        item.RegistrationData.Sheet = item.RegistrationData.Sheet || ''
        item.RegistrationData.Folio = item.RegistrationData.Folio || ''
        item.RegistrationData.Section = item.RegistrationData.Section || ''
        item.RegistrationData.Volume = item.RegistrationData.Volume || ''
        item.RegistrationData.AdditionalData = item.RegistrationData.AdditionalData || ''
        return item
      })
      
      const pagination = {
        total: res.meta?.total,
        currentPage: res.meta?.currentPage,
        totalPages: res.meta?.lastPage,
        pageSize: res.meta?.perPage
      }
      this.$store.commit(this.vuexnamespace + '/setPaginationInfo', pagination)
      this.$store.commit('setIsLoading', false)
      this.loadingDataTable = false
      return this.data
    },
    async getMainData (objOrder, filters) {
      const params = {
        paginate: true,
        results: this.pagination.pageSize,
        page: this.pagination.currentPage,
        ...filters
      }
      const data = await getSociedadesPag(objOrder, params)
      if (data.blnError === true) {
        this.doFeedback(i18n.t('feedback.error.formated_api_response_error', data))
      }
      return data
    },
    applyFiltersHandler: async function (eventData) {
      this.unselect()
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.data = await this.applyFilters(eventData)
    },
    changeCurrentPage: async function (event) {
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', event)
      this.data = await this.applyFilters(null)
    },
    changePerPage: async function (event) {
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.$store.commit(this.vuexnamespace + '/setPageSize', event)
      this.kendo.take = event
      this.data = await this.applyFilters(null)
    },
    removeFilterTagHanlder: function (tag) {
      this.unselect()
      // this.$store.commit(this.vuexnamespace + '/removeTag', tag)
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      const filter = this.$store.getters[this.vuexnamespace + '/getAppliedFilters']
      delete filter[tag[0]]
      this.$store.commit(this.vuexnamespace + '/setMasterFilters', filter)
      this.applyFiltersHandler(null)
    },
    headerCoreFiltersHandler: function (obs) {
      if (this.isHeadCoreReady) {
        this.applyFilters(null)
      }
    },
    removeAllTagsHandler: function () {
      this.unselect()
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.$store.commit(this.vuexnamespace + '/resetMasterFilters')
      this.applyFiltersHandler(null)
    },
    unselect: function () {
      this.selected = []
    },
    executeAction: async function (action) {
      this.selectedAction = action
      if (this.selectedAction.IdAccion === 0) { // borrar
        this.deleteContacto()
      } else if (this.selectedAction.IdAccion === 12) {
        this.toggleModalGroupingSeller()
      } else if (this.selectedAction.IdAccion === 13) {
        this.toggleModalGroupingBuyer()
      } else {
        console.error(i18n.t('feedback.error.unexpected_action_error', { action: JSON.stringify(action) }))
      }
    },
    deleteContacto: async function () {
      this.showModal = !this.showModal
    },
    goToNewContact: function () {
      this.$router.push({ name: 'masters/society/details', params: { id: -1 } })
    },
    deleteHandler: async function () {
      try {
        this.$store.commit('setIsLoading', true)
        const payload ={
          IdSociedades: this.selected || []
        }
        const response = await deleteSociedad(payload)
        if (response.blnError === true) {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
        } else {
          this.doFeedback(i18n.t('feedback.success.action_tramited_msg'), 'success')
          this.unselect()
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
      this.data = await this.applyFilters()
    },
    saveTagsHandler: function () {
      this.saveSearchDialog = !this.saveSearchDialog
    },
    toggleDialogSearches: function () {
      this.saveSearchDialog = !this.saveSearchDialog
    },
    applySearchHandler: async function () {
      this.toggleDialogSearches()
      // Manda actualizar lista de búsquedas
      this.$refs.filterPanelSearches.updateData()
    },
    updateNumberSearches: async function (number) {
      this.numberSearches = number
    },
  }
}
</script>
